/**
 * ParkingSpotSelector
 *
 * Zone-grouped parking-spot grid (not a yard map — spots have no coordinates).
 * Used in PauseWorkDialog and VehicleLocationDialog.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  useParkingCurrentStatus,
  type ParkingSpotStatus,
} from '@/hooks/useParking';

interface ParkingSpotSelectorProps {
  selectedZoneId: string;
  selectedSpotId: string;
  onZoneChange: (zoneId: string) => void;
  onSpotChange: (spotId: string) => void;
}

function sortSpots(spots: ParkingSpotStatus[]): ParkingSpotStatus[] {
  return spots
    .slice()
    .sort((a, b) => a.identifier.localeCompare(b.identifier, undefined, { numeric: true }));
}

function spotStatusLabel(
  t: (key: string, options?: Record<string, string>) => string,
  spot: ParkingSpotStatus,
  selected: boolean,
): string {
  if (spot.isOccupied) {
    return spot.vehicleReg
      ? t('vehicleLocation.spotOccupiedBy', { reg: spot.vehicleReg })
      : t('vehicleLocation.spotOccupied');
  }
  return selected
    ? t('vehicleLocation.spotSelected')
    : t('vehicleLocation.spotAvailable');
}

function spotAriaLabel(
  t: (key: string, options?: Record<string, string>) => string,
  spot: ParkingSpotStatus,
  selected: boolean,
): string {
  if (spot.isOccupied) {
    return spot.vehicleReg
      ? t('vehicleLocation.spotAriaOccupiedBy', {
          identifier: spot.identifier,
          reg: spot.vehicleReg,
        })
      : t('vehicleLocation.spotAriaOccupied', { identifier: spot.identifier });
  }
  return selected
    ? t('vehicleLocation.spotAriaSelected', { identifier: spot.identifier })
    : t('vehicleLocation.spotAriaAvailable', { identifier: spot.identifier });
}

export function ParkingSpotSelector({
  selectedZoneId,
  selectedSpotId,
  onZoneChange,
  onSpotChange,
}: ParkingSpotSelectorProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { data, isLoading, isError } = useParkingCurrentStatus();

  const handleSelect = (zoneId: string, spotId: string) => {
    onZoneChange(zoneId);
    onSpotChange(spotId);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={28} aria-label={tc('loading')} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography variant="body2" color="error">
        {tc('error')}
      </Typography>
    );
  }

  const zones = data?.zones ?? [];

  if (zones.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('vehicleLocation.noParkingZones')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5} role="group" aria-label={t('vehicleLocation.selectParkingSpot')}>
      {zones.map((zone) => {
        const spots = sortSpots(zone.spots);
        return (
          <Box key={zone.zoneId} role="group" aria-label={zone.zoneName}>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>
              {zone.zoneName}
            </Typography>
            {spots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('pauseDialog.noSpotsAvailable')}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(5.5rem, 1fr))',
                  gap: 1,
                }}
              >
                {spots.map((spot) => {
                  const selected =
                    selectedSpotId === spot.spotId && selectedZoneId === zone.zoneId;
                  const occupied = spot.isOccupied;
                  return (
                    <Button
                      key={spot.spotId}
                      type="button"
                      variant={selected ? 'contained' : 'outlined'}
                      color={occupied ? 'error' : 'success'}
                      disabled={occupied}
                      aria-pressed={selected}
                      aria-label={spotAriaLabel(t, spot, selected)}
                      onClick={() => handleSelect(zone.zoneId, spot.spotId)}
                      sx={{
                        width: '100%',
                        minHeight: 56,
                        px: 0.75,
                        py: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.25,
                        textTransform: 'none',
                        lineHeight: 1.2,
                        borderWidth: selected ? 2 : 1,
                        '&.Mui-disabled': {
                          opacity: 1,
                          color: 'text.secondary',
                          borderColor: 'error.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>
                        {spot.identifier}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ maxWidth: '100%', color: 'inherit', lineHeight: 1.2 }}
                      >
                        {spotStatusLabel(t, spot, selected)}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
